/**
 * Theme Schema
 *
 * Zod schemas and validation utilities for theme and color scheme.
 */

import { z } from 'zod';

/* ============================================================================
   Theme Schema
   ========================================================================= */

/**
 * Available theme options
 */
export const ThemeSchema = z.enum(['light', 'dark', 'tron']);

export type Theme = z.infer<typeof ThemeSchema>;

/**
 * Default theme when no preference is set
 */
export const DEFAULT_THEME: Theme = 'light';

/**
 * Theme cookie configuration
 */
export const THEME_COOKIE_NAME = 'theme';

/* ============================================================================
   Color Scheme Schema
   ========================================================================= */

/**
 * Available color scheme options for accessibility
 */
export const ColorSchemeSchema = z.enum(['default', 'colorblind']);

export type ColorScheme = z.infer<typeof ColorSchemeSchema>;

/**
 * Default color scheme when no preference is set
 */
export const DEFAULT_COLOR_SCHEME: ColorScheme = 'default';

/**
 * Color scheme cookie configuration
 */
export const COLOR_SCHEME_COOKIE_NAME = 'color-scheme';

/* ============================================================================
   Validation Helpers
   ========================================================================= */

/**
 * Safely parse theme from unknown value
 * Returns default theme if validation fails
 */
export function parseTheme(value: unknown): Theme {
  const result = ThemeSchema.safeParse(value);
  return result.success ? result.data : DEFAULT_THEME;
}

/**
 * Check if a value is a valid theme
 */
export function isValidTheme(value: unknown): value is Theme {
  return ThemeSchema.safeParse(value).success;
}

/**
 * Safely parse color scheme from unknown value
 * Returns default color scheme if validation fails
 */
export function parseColorScheme(value: unknown): ColorScheme {
  const result = ColorSchemeSchema.safeParse(value);
  return result.success ? result.data : DEFAULT_COLOR_SCHEME;
}

/**
 * Check if a value is a valid color scheme
 */
export function isValidColorScheme(value: unknown): value is ColorScheme {
  return ColorSchemeSchema.safeParse(value).success;
}

/**
 * Attribute names for theme and color scheme
 */
export const THEME_ATTRIBUTE = 'data-theme';
export const COLOR_THEME_ATTRIBUTE = 'data-color-scheme';
